import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import openSocket from "socket.io-client";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	CircularProgress,
	Dialog,
	DialogContent,
	IconButton,
	Typography,
	makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const TAMANHO_QR = 264;

const useStyles = makeStyles(theme => ({
	// Cantos arredondados e sem a "cara" de Dialog padrão, no estilo da tela de
	// pareamento do WhatsApp Web.
	paper: {
		borderRadius: 14,
		maxWidth: 760,
	},

	conteudo: {
		display: "flex",
		alignItems: "center",
		gap: 40,
		padding: "36px 40px",
		// Em tela estreita/zoom as duas colunas não caberiam lado a lado: empilha
		// instruções em cima e QR embaixo.
		[theme.breakpoints.down("xs")]: {
			flexDirection: "column",
			gap: 24,
			padding: 24,
		},
	},

	instrucoes: {
		flex: 1,
		minWidth: 0,
	},

	titulo: {
		fontWeight: 400,
		marginBottom: 4,
	},

	conexao: {
		color: theme.palette.text.secondary,
		marginBottom: 20,
	},

	passos: {
		listStyle: "none",
		margin: 0,
		padding: 0,
	},

	passo: {
		display: "flex",
		alignItems: "flex-start",
		marginBottom: 14,
	},

	// Círculo numerado à esquerda de cada passo (igual WhatsApp Web).
	numero: {
		flex: "none",
		width: 22,
		height: 22,
		marginRight: 12,
		borderRadius: "50%",
		border: `1px solid ${theme.palette.divider}`,
		color: theme.palette.text.secondary,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		lineHeight: 1,
	},

	textoPasso: {
		paddingTop: 3,
	},

	rodape: {
		display: "block",
		marginTop: 18,
		color: theme.palette.text.secondary,
	},

	// O QR fica sempre em fundo claro, inclusive no dark mode: leitor de câmera
	// precisa do contraste padrão (escuro sobre claro) pra reconhecer o código.
	areaQr: {
		flex: "none",
		width: TAMANHO_QR + 24,
		height: TAMANHO_QR + 24,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#ffffff",
		borderRadius: 10,
		border: `1px solid ${theme.palette.divider}`,
	},

	aguardando: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: 12,
		color: "#54656f",
	},

	fechar: {
		position: "absolute",
		top: 8,
		right: 8,
		color: theme.palette.text.secondary,
	},
}));

// As instruções destacam os nomes dos menus do WhatsApp; **assim** vira negrito.
const comDestaques = texto =>
	String(texto)
		.split("**")
		.map((parte, i) => (i % 2 ? <strong key={i}>{parte}</strong> : parte));

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const [qrCode, setQrCode] = useState("");
	const [nomeConexao, setNomeConexao] = useState("");
	const { user } = useContext(AuthContext);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`/whatsapp/${whatsAppId}`);
				setQrCode(data.qrcode);
				setNomeConexao(data.name || "");
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	useEffect(() => {
		if (!whatsAppId) return;
		let urlsUnicas = [];
		if (user && JSON.stringify(user) !== '{}') {
			urlsUnicas = new Set([process.env.REACT_APP_BACKEND_URL]);
			user.whatsapps
				.map(w => w.urlBackend)
				.filter(Boolean)
				.forEach(url => urlsUnicas.add(url));
			urlsUnicas = [...urlsUnicas];
		} else {
			console.error("USUARIO NÃO ENCONTRADO NO SOCKET");
		}
		const sockets = [];
		urlsUnicas.forEach(url => {
			const socket = openSocket(url.trim() + "/");
			sockets.push(socket);

			// O evento é global (io.emit): chega um por conexão a cada mudança de estado.
			// Sem filtrar pelo id, qualquer outra conexão subindo fechava esta modal.
			socket.on("whatsappSession", data => {
				if (data.action !== "update" || data.session.id !== whatsAppId) return;

				setQrCode(data.session.qrcode);

				// Sem qrcode = pareou ou a sessão caiu; não há mais o que ler.
				if (data.session.qrcode === "") {
					onClose();
				}
			});
		});
		return () => {
			sockets.forEach(socket => socket.disconnect());
		};
	}, [whatsAppId, onClose]);

	const passos = [
		i18n.t("qrCode.step1"),
		i18n.t("qrCode.step2"),
		i18n.t("qrCode.step3"),
		i18n.t("qrCode.step4"),
	];

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" classes={{ paper: classes.paper }}>
			<IconButton size="small" className={classes.fechar} onClick={onClose} title="Fechar">
				<CloseIcon fontSize="small" />
			</IconButton>
			<DialogContent className={classes.conteudo}>
				<div className={classes.instrucoes}>
					<Typography variant="h5" className={classes.titulo}>
						{i18n.t("qrCode.title")}
					</Typography>
					{nomeConexao && (
						<Typography variant="body2" className={classes.conexao}>
							{i18n.t("qrCode.connection")}: <strong>{nomeConexao}</strong>
						</Typography>
					)}
					<ol className={classes.passos}>
						{passos.map((passo, i) => (
							<li key={i} className={classes.passo}>
								<Typography variant="body2" component="span" className={classes.numero}>
									{i + 1}
								</Typography>
								<Typography variant="body2" component="span" className={classes.textoPasso}>
									{comDestaques(passo)}
								</Typography>
							</li>
						))}
					</ol>
					<Typography variant="caption" className={classes.rodape}>
						{i18n.t("qrCode.expires")}
					</Typography>
				</div>

				<div className={classes.areaQr}>
					{qrCode ? (
						<QRCode
							value={qrCode}
							size={TAMANHO_QR}
							level="M"
							renderAs="svg"
							bgColor="#ffffff"
							fgColor="#111b21"
						/>
					) : (
						<div className={classes.aguardando}>
							<CircularProgress size={28} />
							<Typography variant="body2">{i18n.t("qrCode.waiting")}</Typography>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default React.memo(QrcodeModal);
